import { useEffect, useState, type ReactNode } from "react";
import { API_BASE } from "@/config/api";
import { SITE_TEXTS, type SiteTextDef, type SiteTextKey } from "@/lib/siteTexts";

const defs: readonly SiteTextDef[] = SITE_TEXTS;
const inputCls = "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm";
const labelCls = "block text-xs font-medium text-foreground-600 mb-1";

interface Props {
  title: string;
  description?: string;
  keys: SiteTextKey[];
  // Opciones propias de la sección que se guardan junto con los textos con el mismo botón.
  children?: ReactNode;
  onSaveExtra?: () => Promise<boolean>;
}

type Overrides = Record<string, string>;

async function fetchOverrides(): Promise<Overrides> {
  const res = await fetch(`${API_BASE}/bootstrap`);
  const json = await res.json();
  return json.contentBlocks?.textos?.site?.[0]?.values ?? {};
}

// Edita unos pocos textos de "Textos del sitio" dentro del modulo donde el usuario los busca.
// Guarda en el mismo lugar que esa pantalla, sin tocar los demas textos ya modificados.
export default function SiteTextsPanel({ title, description, keys, children, onSaveExtra }: Props) {
  const fields = keys.map((k) => defs.find((d) => d.key === k)).filter((d): d is SiteTextDef => !!d);
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  useEffect(() => {
    fetchOverrides()
      .then((overrides) => {
        const next: Record<string, string> = {};
        for (const d of fields) next[d.key] = typeof overrides[d.key] === "string" ? overrides[d.key] : d.default;
        setValues(next);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSave() {
    setMessage(null);
    const empty = fields.filter((d) => !d.optional && !(values[d.key] ?? "").trim());
    if (empty.length > 0) {
      setMessage({ type: "error", text: `«${empty[0].label}» no puede quedar vacío.` });
      return;
    }
    setSaving(true);
    try {
      if (onSaveExtra && !(await onSaveExtra())) {
        setMessage({ type: "error", text: "No se pudo guardar" });
        return;
      }
      const current = await fetchOverrides();
      const merged: Overrides = { ...current };
      for (const d of fields) {
        if (values[d.key] === d.default) delete merged[d.key];
        else merged[d.key] = values[d.key];
      }
      const res = await fetch(`${API_BASE}/content-blocks/textos/site`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: [{ id: "config", values: merged }] }),
      });
      setMessage(
        res.ok
          ? { type: "success", text: "Textos guardados. Los cambios ya se ven en el sitio." }
          : { type: "error", text: "No se pudo guardar" }
      );
    } catch {
      setMessage({ type: "error", text: "Error de conexión." });
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="rounded-lg border border-background-200 bg-background-50 p-5 space-y-3" data-site-texts-panel>
      <div>
        <h2 className="font-heading font-semibold text-foreground-900">{title}</h2>
        {description && <p className="mt-1 text-xs text-foreground-500">{description}</p>}
      </div>

      {loading ? (
        <p className="text-sm text-foreground-500">Cargando...</p>
      ) : (
        <>
          {fields.map((d) => (
            <div key={d.key} data-text-key={d.key}>
              <label className={labelCls}>
                {d.label}
                {d.optional && <span className="font-normal text-foreground-400"> — déjalo vacío para ocultarlo</span>}
              </label>
              {d.kind === "textarea" ? (
                <textarea
                  className={inputCls}
                  rows={3}
                  value={values[d.key] ?? ""}
                  onChange={(e) => setValues((v) => ({ ...v, [d.key]: e.target.value }))}
                />
              ) : (
                <input
                  className={inputCls}
                  value={values[d.key] ?? ""}
                  onChange={(e) => setValues((v) => ({ ...v, [d.key]: e.target.value }))}
                />
              )}
              {values[d.key] !== d.default && (
                <p className="mt-1 text-xs text-foreground-500">
                  <button
                    type="button"
                    onClick={() => setValues((v) => ({ ...v, [d.key]: d.default }))}
                    className="font-medium text-primary-600 hover:text-primary-700 cursor-pointer"
                  >
                    Restaurar original
                  </button>
                  <span className="text-foreground-400"> · Original: {d.default}</span>
                </p>
              )}
            </div>
          ))}

          {children}

          {message && (
            <div
              className={`rounded-md p-3 text-sm border ${
                message.type === "success"
                  ? "bg-primary-100 border-primary-200 text-primary-800"
                  : "bg-accent-100 border-accent-200 text-accent-800"
              }`}
            >
              {message.text}
            </div>
          )}

          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-md bg-primary-500 px-5 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-60 cursor-pointer"
          >
            {saving ? "Guardando..." : "Guardar textos"}
          </button>
        </>
      )}
    </section>
  );
}
